import { Box } from '@mui/material'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { ContextMenu } from '@/components'
import { CONTAINER_TYPES, StructureTreeItem, TreeV2, TreeV2Handle } from '@/components/TreeV2'
import { StructureDto, StructureObjectDto } from '@/endpoints/models'
import { useAppDispatch, useAppStore, useLocalStore } from '@/hooks'
import { useRunSaveBeforeLeave } from '@/pages/User/pages/Home/context/SaveBeforeLeaveContext'
import { collapseNode, expandNode } from '@/store/modules/node/actions'
import { NODE_LOAD_CHILDREN, NODE_LOAD_NESTED_CHILDREN, NODE_SET_SELECTED } from '@/store/modules/node/constants'
import { openTab } from '@/store/modules/tab/actions'
import {
	hasStructureEditPermission,
	isPrivilegedUser,
} from '@/utils/locking'

import { EntityTypeIcon } from '../../components/EntityTypeIcon'
import { Header } from './components/Header'
import { ItemMenu } from './components/ItemMenu'
import { NodeActions } from './components/NodeActions/NodeActions'
import { StructureAclModal } from './components/StructureAclModal'
import { useDragHandlerV2 } from './dragV2'
import { Container, TreeContainer } from './styles'
import { useTreePanelCollapsed, useTreePanelOperations } from './TreePanelContext'
import { TREE_PANEL_ORDER } from './utils'

const toStructureDtoType = (type: string): StructureObjectDto.TypeEnum | undefined =>
	type as StructureObjectDto.TypeEnum

const findRootSystemId = (
	nodeId: number,
	nodes: Record<number, StructureDto | undefined>,
): string | null => {
	let current = nodes[nodeId]
	const visited = new Set<number>()

	while (current && !visited.has(current.id)) {
		visited.add(current.id)
		if (current.type === StructureObjectDto.TypeEnum.SYSTEM) {
			return String(current.id)
		}
		if (current.parentStructureId == null) break
		current = nodes[current.parentStructureId]
	}

	return null
}

const NodeActionsWithRedux = ({ structureId, item }: { item: StructureTreeItem; structureId: number }) => {
	const reduxNode = useAppStore((state) => state.node.nodes[structureId])
	return (
		<NodeActions
			node={{
				key: structureId,
				title: item.label,
				node: {
					id: structureId,
					name: item.label,
					code: item.code || '',
					type: toStructureDtoType(item.type)!,
					status: item.status,
					lockUserId: reduxNode
						? (reduxNode.lockUserId ?? undefined)
						: item.lockedFor?.id,
					lockUserName: reduxNode
						? (reduxNode.lockUserName ?? undefined)
						: item.lockedFor?.fullName,
					lockTime: reduxNode ? (reduxNode.lockTime ?? undefined) : item.lockedTime,
				} as StructureDto,
			}}
		/>
	)
}

const reduxNodeToTreeItem = (node: StructureDto): StructureTreeItem => ({
	id: String(node.id),
	structureId: node.id,
	label: node.name || node.code || '',
	code: node.code,
	type: node.type as StructureTreeItem['type'],
	status: node.status,
	parentStructureId: node.parentStructureId,
	lockedFor: node.lockUserId ? { id: node.lockUserId, fullName: node.lockUserName } : undefined,
	lockedTime: node.lockTime,
	hasChildren: node.hasChildren,
	structurePermissions: node.structurePermissions,
})

const toStructureDto = (
	item: StructureTreeItem,
	reduxNode?: StructureDto | null,
	reduxChildIds?: number[],
): StructureDto => {
	const perms = item.structurePermissions
	const dto: StructureDto = {
		id: item.structureId,
		name: item.label,
		code: item.code || '',
		type: toStructureDtoType(item.type)!,
		hasChildren:
			Boolean(item.hasChildren) ||
			Boolean(reduxNode?.hasChildren) ||
			(reduxChildIds?.length ?? 0) > 0 ||
			(item.hasChildren == null &&
				reduxNode?.hasChildren == null &&
				CONTAINER_TYPES.includes(item.type)),
		companyId: reduxNode?.companyId ?? 0,
		canEdit:
			perms !== undefined
				? perms.includes('EDIT')
				: Boolean(reduxNode?.canEdit),
		status: item.status,
		parentStructureId: item.parentStructureId,
		lockUserId: item.lockedFor?.id,
		lockUserName: item.lockedFor?.fullName,
		lockTime: item.lockedTime,
		structurePermissions: perms ?? reduxNode?.structurePermissions,
	}

	return {
		...dto,
		canEdit: hasStructureEditPermission(dto),
	}
}

/** Only block when ACL is explicit and omits VIEW. Empty array = not loaded / unknown — do not block. */
const isStructureViewDenied = (
	perms: string[] | undefined | null,
): boolean => {
	if (isPrivilegedUser()) return false

	return (
		Array.isArray(perms) &&
		perms.length > 0 &&
		!perms.includes('VIEW')
	)
}

const TreePanelV2Component = () => {
	const dispatch = useAppDispatch()
	const runSaveBeforeLeave = useRunSaveBeforeLeave()
	const selectedId = useAppStore((state) => state.tab.selectedId)
	const selectMode = useAppStore((state) => state.node.selectMode)
	const selectedNodes = useAppStore((state) => state.node.selectedNodes)
	const reduxExpanded = useAppStore((state) => state.node.expanded)
	const reduxRoots = useAppStore((state) => state.node.roots)
	const reduxChildren = useAppStore((state) => state.node.children)
	const reduxNodes = useAppStore((state) => state.node.nodes)
	const treeRef = useRef<TreeV2Handle>(null)
	const {
		registerTreeOperations,
		resetTreeOperations,
		pendingRevealId,
		clearPendingRevealId,
		pendingSystemContextMenu,
		clearPendingSystemContextMenu,
		revealItem: revealItemInTree,
		selectedSystemId,
		setSelectedSystemId,
	} = useTreePanelOperations()
	const { isCollapsed: isTreeHidden, setCollapsed: setIsTreeHidden } = useTreePanelCollapsed()
	const [alphabetSort, setAlphabetSort] = useLocalStore(TREE_PANEL_ORDER, true)
	const [isRevealing, setIsRevealing] = useState(false)
	const [isFullTreeLoading, setIsFullTreeLoading] = useState(true)
	const [contextMenuOpen, setContextMenuOpen] = useState(false)
	const [contextMenuNode, setContextMenuNode] = useState<StructureTreeItem | null>(null)
	const [contextMenuPos, setContextMenuPos] = useState({ x: 0, y: 0 })
	const [aclModalNode, setAclModalNode] = useState<StructureDto | null>(null)
	const [aclModalTargetId, setAclModalTargetId] = useState<number | null>(null)
	const selectedSystemIdRef = useRef(selectedSystemId)
	const reduxNodesRef = useRef(reduxNodes)
	selectedSystemIdRef.current = selectedSystemId
	reduxNodesRef.current = reduxNodes

	const handleFullTreeLoadingChange = useCallback((loading: boolean) => {
		setIsFullTreeLoading(loading)
	}, [])

	const expandedItems = useMemo(() => reduxExpanded.map(String), [reduxExpanded])

	// Restore tree from Redux when returning to Home (e.g. from Settings) so data is visible immediately
	const initialSystems = useMemo(() => {
		if (reduxRoots.length === 0) return undefined
		return reduxRoots
			.map((id) => {
				const node = reduxNodes[id]
				if (!node) return null
				const item = reduxNodeToTreeItem(node)
				item.hasChildren = (reduxChildren[String(id)]?.length ?? 0) > 0
				return item
			})
			.filter(Boolean) as StructureTreeItem[]
	}, [reduxRoots, reduxNodes, reduxChildren])

	const initialChildrenMapRef = useRef<Record<string, StructureTreeItem[]> | null>(null)
	const initialChildrenMap = useMemo(() => {
		if (initialChildrenMapRef.current !== null) return initialChildrenMapRef.current
		if (Object.keys(reduxChildren).length === 0) return undefined

		const result: Record<string, StructureTreeItem[]> = {}
		Object.entries(reduxChildren).forEach(([parentId, childIds]) => {
			const children = childIds.map((id) => reduxNodes[id]).filter(Boolean).map(reduxNodeToTreeItem)
			if (children.length > 0) result[parentId] = children
		})
		// Set hasChildren based on whether item has children in cache (not stale Redux value)
		Object.values(result).forEach((children) => {
			children.forEach((child) => {
				child.hasChildren = (result[child.id]?.length ?? 0) > 0
			})
		})
		initialChildrenMapRef.current = result
		return result
	}, [reduxChildren, reduxNodes])

	// Capture expanded IDs at mount for session restore (wait for non-empty value)
	const initialExpandedIdsRef = useRef<string[] | null>(null)
	const initialExpandedIds = useMemo(() => {
		if (initialExpandedIdsRef.current !== null) return initialExpandedIdsRef.current
		// Only capture once we have expanded items from Redux
		if (expandedItems.length > 0) {
			initialExpandedIdsRef.current = expandedItems
		}
		return expandedItems
	}, [expandedItems])

	useEffect(() => {
		registerTreeOperations({
			refreshSystems: async () => await treeRef.current?.refreshSystems(),
			refreshSystemsSilent: async () => await treeRef.current?.refreshSystemsSilent(),
			refreshParent: async (parentId: string) => await treeRef.current?.refreshParent(parentId),
			refreshParentSilent: async (parentId: string) => await treeRef.current?.refreshParentSilent(parentId),
			revealItem: async (itemId: string) => await treeRef.current?.revealItem(itemId),
			updateNodes: (nodeIds, updates) => treeRef.current?.updateNodes(nodeIds, updates),
			openSelectedSystemContextMenu: (x, y) => {
				const id = selectedSystemIdRef.current
				if (!id) return
				const node = reduxNodesRef.current[Number(id)]
				if (!node) return
				setContextMenuNode(reduxNodeToTreeItem(node))
				setContextMenuPos({ x, y })
				setContextMenuOpen(true)
			},
		})
		return () => {
			resetTreeOperations()
		}
	}, [registerTreeOperations, resetTreeOperations])

	useEffect(() => {
		if (!pendingSystemContextMenu) return
		const { x, y } = pendingSystemContextMenu
		clearPendingSystemContextMenu()
		const id = selectedSystemIdRef.current
		if (!id) return
		const node = reduxNodesRef.current[Number(id)]
		if (!node) return
		setContextMenuNode(reduxNodeToTreeItem(node))
		setContextMenuPos({ x, y })
		setContextMenuOpen(true)
	}, [pendingSystemContextMenu, clearPendingSystemContextMenu])

	useEffect(() => {
		if (!pendingRevealId || isFullTreeLoading) return

		let cancelled = false
		const runReveal = async () => {
			const systemId = findRootSystemId(Number(pendingRevealId), reduxNodes)
			if (systemId && systemId !== selectedSystemId) {
				setSelectedSystemId(systemId)
			}
			await revealItemInTree(pendingRevealId)
			if (!cancelled) clearPendingRevealId()
		}

		void runReveal()
		return () => {
			cancelled = true
		}
	}, [
		pendingRevealId,
		isFullTreeLoading,
		revealItemInTree,
		clearPendingRevealId,
		reduxNodes,
		selectedSystemId,
		setSelectedSystemId,
	])

	useEffect(() => {
		if (!selectedSystemId) return
		if (expandedItems.includes(selectedSystemId)) return
		dispatch(expandNode(Number(selectedSystemId)))
	}, [dispatch, expandedItems, selectedSystemId])

	const handleRevealInTree = useCallback(async () => {
		if (!selectedId) return
		setIsRevealing(true)
		await treeRef.current?.revealItem(String(selectedId))
		setIsRevealing(false)
	}, [selectedId])

	const handleExpandedItemsChange = useCallback(
		(itemIds: string[]) => {
			const newSet = new Set(itemIds)
			const oldSet = new Set(expandedItems)
			itemIds.filter((id) => !oldSet.has(id)).forEach((id) => dispatch(expandNode(Number(id))))
			expandedItems.filter((id) => !newSet.has(id)).forEach((id) => dispatch(collapseNode(Number(id))))
		},
		[dispatch, expandedItems],
	)

	const handleExpandItem = useCallback(
		(itemId: string) => {
			if (!expandedItems.includes(itemId)) dispatch(expandNode(Number(itemId)))
		},
		[dispatch, expandedItems],
	)

	const handleRefreshTreeSilent = useCallback(
		() => treeRef.current?.refreshSystemsSilent() ?? Promise.resolve(),
		[],
	)

	const handleHeaderRefresh = useCallback(async () => {
		setIsFullTreeLoading(true)
		try {
			await treeRef.current?.refreshSystems()
		} finally {
			setIsFullTreeLoading(false)
		}
	}, [])

	const getDragDropHandlers = useDragHandlerV2({
		expandedItems,
		onExpandItem: handleExpandItem,
		onRefreshTreeSilent: handleRefreshTreeSilent,
	})

	const handleCheckedItemsChange = useCallback(
		(itemIds: number[]) => {
			dispatch({ type: NODE_SET_SELECTED, metadata: { selectedNodes: itemIds } })
		},
		[dispatch],
	)

	const handleChildrenLoaded = useCallback(
		(parentId: number, children: StructureTreeItem[]) => {
			dispatch({
				type: NODE_LOAD_CHILDREN,
				payload: children.map((c) => toStructureDto(c)),
				metadata: { parentId },
			})
		},
		[dispatch],
	)

	const handleBatchLoaded = useCallback(
		(nodes: StructureTreeItem[]) => {
			dispatch({ type: NODE_LOAD_NESTED_CHILDREN, payload: nodes.map((n) => toStructureDto(n)) })
		},
		[dispatch],
	)

	const handleItemClick = useCallback(
		async (item: StructureTreeItem) => {
			const perms = item.structurePermissions ?? reduxNodes[item.structureId]?.structurePermissions
			if (isStructureViewDenied(perms)) return
			await runSaveBeforeLeave()
			dispatch(openTab(toStructureDto(item, reduxNodes[item.structureId]), true))
		},
		[dispatch, reduxNodes, runSaveBeforeLeave],
	)

	const handleItemDoubleClick = useCallback(
		async (item: StructureTreeItem) => {
			const perms = item.structurePermissions ?? reduxNodes[item.structureId]?.structurePermissions
			if (isStructureViewDenied(perms)) return
			await runSaveBeforeLeave()
			dispatch(openTab(toStructureDto(item, reduxNodes[item.structureId]), false))
		},
		[dispatch, reduxNodes, runSaveBeforeLeave],
	)

	const handleItemContextMenu = useCallback(
		(item: StructureTreeItem, e: React.MouseEvent) => {
			setContextMenuNode(item)
			setContextMenuPos({ x: e.pageX, y: e.pageY })
			setContextMenuOpen(true)
		},
		[],
	)

	const iconComponent = useCallback(
		(item: StructureTreeItem, isExpanded: boolean) => (
			<EntityTypeIcon type={toStructureDtoType(item.type)} expanded={isExpanded} />
		),
		[],
	)

	const actionComponent = useCallback(
		(item: StructureTreeItem) => <NodeActionsWithRedux structureId={item.structureId} item={item} />,
		[],
	)

	const handleRefreshContextNode = useCallback(() => {
		treeRef.current?.refreshSystemsSilent()
	}, [])

	const handleRefreshParentOfContextNode = useCallback(() => {
		treeRef.current?.refreshSystemsSilent()
	}, [])

	const handleContextMenuClose = useCallback(() => {
		setContextMenuOpen(false)
	}, [])

	return (
		<Container role="complementary" data-testid="tree-panel" $collapsed={isTreeHidden}>
			<Header
				alphabet={alphabetSort}
				setAlphabet={setAlphabetSort}
				onRevealInTree={handleRevealInTree}
				canReveal={!!selectedId}
				isFullTreeLoading={isFullTreeLoading}
				isRevealing={isRevealing}
				onHide={() => setIsTreeHidden(true)}
				isHidden={isTreeHidden}
				onShow={() => setIsTreeHidden(false)}
				onTreeRefresh={handleHeaderRefresh}
			/>

			{!isTreeHidden && (
				<TreeContainer>
					<Box sx={{ height: '100%', overflow: 'auto' }}>
						<TreeV2
							ref={treeRef}
							onItemClick={handleItemClick}
							onItemDoubleClick={handleItemDoubleClick}
							onItemContextMenu={handleItemContextMenu}
							onFullTreeLoadingChange={handleFullTreeLoadingChange}
							selectedItemId={selectedId ? String(selectedId) : null}
							expandedItems={expandedItems}
							onExpandedItemsChange={handleExpandedItemsChange}
							onBatchLoaded={handleBatchLoaded}
							onChildrenLoaded={handleChildrenLoaded}
							iconComponent={iconComponent}
							actionComponent={actionComponent}
							getDragDropHandlers={getDragDropHandlers}
							selectMode={selectMode}
							checkedItemIds={selectedNodes}
							onCheckedItemsChange={handleCheckedItemsChange}
							alphabetSort={alphabetSort}
							initialChildrenMap={initialChildrenMap}
							initialSystems={initialSystems}
							initialExpandedIds={initialExpandedIds}
							selectedSystemId={selectedSystemId}
						/>
					</Box>
				</TreeContainer>
			)}

			<ContextMenu opened={contextMenuOpen} x={contextMenuPos.x} y={contextMenuPos.y} onClose={handleContextMenuClose}>
				{contextMenuNode && (
					<ItemMenu
						node={toStructureDto(
							contextMenuNode,
							reduxNodes[contextMenuNode.structureId],
							reduxChildren[contextMenuNode.structureId],
						)}
						onRefresh={handleRefreshContextNode}
						onRefreshParent={handleRefreshParentOfContextNode}
						onOpenAcl={(n) => {
							setAclModalNode(n)
							setAclModalTargetId(null)
							handleContextMenuClose()
						}}
					/>
				)}
			</ContextMenu>
			{aclModalNode?.id != null && (
				<StructureAclModal
					open={!!aclModalNode}
					onClose={() => {
						setAclModalNode(null)
						setAclModalTargetId(null)
					}}
					structureId={aclModalTargetId ?? aclModalNode.id}
					structureName={aclModalNode.name}
					parentStructureId={aclModalNode.parentStructureId}
					parentStructureName={
						aclModalNode.parentStructureId != null
							? reduxNodes[aclModalNode.parentStructureId]?.name ??
								reduxNodes[aclModalNode.parentStructureId]?.code
							: undefined
					}
					onOpenParentPermissions={
						aclModalNode.parentStructureId != null &&
						reduxNodes[aclModalNode.parentStructureId]
							? () => {
									setAclModalNode(reduxNodes[aclModalNode!.parentStructureId!])
									setAclModalTargetId(null)
								}
							: undefined
					}
					onOpenStructurePermissions={(targetStructureId) => {
						const target = reduxNodes[targetStructureId]
						if (target) setAclModalNode(target)
						setAclModalTargetId(targetStructureId)
					}}
				/>
			)}
		</Container>
	)
}

export const TreePanelV2 = React.memo(TreePanelV2Component)
